import React from 'react'
import { PortableText as SanityPortableText } from '@portabletext/react'
import { PortableTextBlock } from '@portabletext/types'
import LazyImage from './LazyImage'
import useSyntaxHighlighting from '@/lib/hooks/useSyntaxHighlighting'
import EnhancedCodeBlock from './CodeBlock'
import RichTextTable from './RichTextTable'
import Callout from './Callout'
import MathEquation from './MathEquation'

// Import Prism syntax highlighting CSS
import 'prismjs/themes/prism-tomorrow.css'
// Import additional languages
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-markdown'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-rust'
import 'prismjs/components/prism-solidity'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-graphql'
import 'prismjs/components/prism-sql'

// Portable Text Code Block Wrapper
const CodeBlock = ({ value }: { value: any }) => {
  return (
    <EnhancedCodeBlock
      code={value.code}
      language={value.language || 'text'}
      filename={value.filename}
      highlightedLines={value.highlightedLines || []}
    />
  )
}

// Custom Embed Component
const EmbedBlock = ({ value }: { value: any }) => {
  const { url, caption } = value

  // Function to get embed HTML based on URL
  const getEmbedHtml = (url: string) => {
    // YouTube embed
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtube.com')
        ? url.split('v=')[1]?.split('&')[0]
        : url.split('youtu.be/')[1]?.split('?')[0]

      if (videoId) {
        return (
          <iframe
            width="100%"
            height="315"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            style={{ border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="rounded-md"
          ></iframe>
        )
      }
    }

    // Twitter embed
    if (url.includes('twitter.com') || url.includes('x.com')) {
      return (
        <div className="twitter-embed">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            View Tweet
          </a>
          <p className="text-sm text-gray-500 mt-1">
            Twitter embeds require JavaScript. Click the link above to view the tweet.
          </p>
        </div>
      )
    }

    // Default fallback for other URLs
    return (
      <div className="embed-fallback">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline"
        >
          {url}
        </a>
      </div>
    )
  }

  return (
    <div className="my-6">
      <div className="embed-container">{getEmbedHtml(url)}</div>
      {caption && <div className="mt-2 text-sm text-gray-500">{caption}</div>}
    </div>
  )
}

// Define custom components for portable text
const components = {
  types: {
    image: ({ value }: any) => {
      if (!value?.asset?._ref) {
        return null
      }
      return (
        <div className="my-6">
          <div className="relative aspect-video w-full">
            <LazyImage
              image={value}
              alt={value.alt || 'Blog post image'}
              fill
              className="rounded-lg object-cover"
              sizes="(max-width: 768px) 100vw, 800px"
            />
          </div>
          {value.caption && <div className="mt-2 text-sm text-gray-500">{value.caption}</div>}
        </div>
      )
    },
    code: ({ value }: any) => <CodeBlock value={value} />,
    embed: ({ value }: any) => <EmbedBlock value={value} />,
    table: ({ value }: any) => <RichTextTable value={value} />,
    callout: ({ value }: any) => <Callout value={value} />,
    math: ({ value }: any) => <MathEquation value={value} />,
  },
  marks: {
    link: ({ children, value }: any) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined
      const target = !value.href.startsWith('/') ? '_blank' : undefined
      return (
        <a href={value.href} rel={rel} target={target} className="text-blue-600 hover:underline">
          {children}
        </a>
      )
    },
    internalLink: ({ children, value }: any) => {
      const { reference } = value
      const href = reference?.slug?.current
        ? `/${reference._type === 'project' ? 'projects' : 'blog'}/${reference.slug.current}`
        : '#'

      return (
        <a href={href} className="text-blue-600 hover:underline">
          {children}
        </a>
      )
    },
    code: ({ children }: any) => {
      return (
        <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      )
    },
    highlight: ({ children }: any) => {
      return <span className="bg-yellow-100 dark:bg-yellow-900 px-1">{children}</span>
    },
    underline: ({ children }: any) => {
      return <span className="underline">{children}</span>
    },
    'strike-through': ({ children }: any) => {
      return <span className="line-through">{children}</span>
    },
  },
  block: {
    h1: ({ children }: any) => <h1 className="text-4xl font-bold mt-8 mb-4">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-3xl font-bold mt-8 mb-4">{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-2xl font-bold mt-6 mb-3">{children}</h3>,
    h4: ({ children }: any) => <h4 className="text-xl font-bold mt-6 mb-2">{children}</h4>,
    h5: ({ children }: any) => <h5 className="text-lg font-bold mt-4 mb-2">{children}</h5>,
    h6: ({ children }: any) => <h6 className="text-base font-bold mt-4 mb-2">{children}</h6>,
    normal: ({ children }: any) => <p className="mb-4 leading-relaxed">{children}</p>,
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-6">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }: any) => <ul className="list-disc pl-6 mb-6 space-y-2">{children}</ul>,
    number: ({ children }: any) => <ol className="list-decimal pl-6 mb-6 space-y-2">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }: any) => <li>{children}</li>,
    number: ({ children }: any) => <li>{children}</li>,
  },
}

interface PortableTextProps {
  content: PortableTextBlock[]
  className?: string
}

const PortableText = ({ content, className = '' }: PortableTextProps) => {
  // Use our custom hook for syntax highlighting
  useSyntaxHighlighting([content])

  if (!content) {
    return null
  }

  return (
    <div className={`prose prose-lg dark:prose-invert max-w-none ${className}`}>
      <SanityPortableText value={content} components={components} />
    </div>
  )
}

// Add custom CSS for code highlighting
const PortableTextWithStyles = (props: PortableTextProps) => {
  return (
    <>
      <style jsx global>{`
        /* Custom styles for code blocks */
        .code-block {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* Highlighted lines */
        .code-block .highlight-line {
          background-color: rgba(255, 255, 0, 0.1);
          display: block;
        }

        /* Adjust Prism theme for dark mode */
        .dark .code-block pre[class*='language-'] {
          background-color: #1e1e1e;
        }

        /* Improve code readability */
        .code-block pre[class*='language-'] {
          padding: 1.25rem;
          margin: 0;
          overflow: auto;
          font-size: 0.9rem;
          line-height: 1.6;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .code-block pre[class*='language-'] {
            padding: 1rem;
            font-size: 0.8rem;
          }
        }
      `}</style>
      <PortableText {...props} />
    </>
  )
}

export default PortableTextWithStyles
